// PATCH oct18-tax: shared tax and currency helpers (company settings, quotes, sign up)

export var COUNTRIES = [
  { code: "CA", name: "Canada" },
  { code: "US", name: "United States" },
  { code: "OT", name: "Other country" },
];

// Canadian rates as of October 2026. Owners can edit any rate in Settings.
export var CA_REGIONS = [
  { code: "AB", name: "Alberta", lines: [{ name: "GST", rate: 5 }] },
  { code: "BC", name: "British Columbia", lines: [{ name: "GST", rate: 5 }, { name: "PST", rate: 7 }] },
  { code: "MB", name: "Manitoba", lines: [{ name: "GST", rate: 5 }, { name: "RST", rate: 7 }] },
  { code: "NB", name: "New Brunswick", lines: [{ name: "HST", rate: 15 }] },
  { code: "NL", name: "Newfoundland and Labrador", lines: [{ name: "HST", rate: 15 }] },
  { code: "NS", name: "Nova Scotia", lines: [{ name: "HST", rate: 14 }] },
  { code: "NT", name: "Northwest Territories", lines: [{ name: "GST", rate: 5 }] },
  { code: "NU", name: "Nunavut", lines: [{ name: "GST", rate: 5 }] },
  { code: "ON", name: "Ontario", lines: [{ name: "HST", rate: 13 }] },
  { code: "PE", name: "Prince Edward Island", lines: [{ name: "HST", rate: 15 }] },
  { code: "QC", name: "Quebec", lines: [{ name: "GST", rate: 5 }, { name: "QST", rate: 9.975 }] },
  { code: "SK", name: "Saskatchewan", lines: [{ name: "GST", rate: 5 }, { name: "PST", rate: 6 }] },
  { code: "YT", name: "Yukon", lines: [{ name: "GST", rate: 5 }] },
];

// US sales tax depends on state AND city, so no rate is filled in.
var US_NAMES = ["AL Alabama","AK Alaska","AZ Arizona","AR Arkansas","CA California","CO Colorado","CT Connecticut","DE Delaware","DC District of Columbia","FL Florida","GA Georgia","HI Hawaii","ID Idaho","IL Illinois","IN Indiana","IA Iowa","KS Kansas","KY Kentucky","LA Louisiana","ME Maine","MD Maryland","MA Massachusetts","MI Michigan","MN Minnesota","MS Mississippi","MO Missouri","MT Montana","NE Nebraska","NV Nevada","NH New Hampshire","NJ New Jersey","NM New Mexico","NY New York","NC North Carolina","ND North Dakota","OH Ohio","OK Oklahoma","OR Oregon","PA Pennsylvania","RI Rhode Island","SC South Carolina","SD South Dakota","TN Tennessee","TX Texas","UT Utah","VT Vermont","VA Virginia","WA Washington","WV West Virginia","WI Wisconsin","WY Wyoming"];
export var US_REGIONS = US_NAMES.map(function (s) {
  return { code: s.slice(0, 2), name: s.slice(3), lines: [{ name: "Sales Tax", rate: 0 }] };
});

export var CURRENCIES = ["CAD", "USD", "GBP", "EUR", "AUD", "NZD", "INR", "PKR", "AED"];

export var DEFAULT_TAX = { country: "CA", region: null, lines: [{ name: "GST", rate: 5 }], mode: "added", currency: "CAD" };

export function regionsFor(country) {
  if (country === "CA") return CA_REGIONS;
  if (country === "US") return US_REGIONS;
  return [];
}

// Tax lines and currency to start with for a country and province or state.
export function presetFor(country, region) {
  var list = regionsFor(country);
  var r = null;
  for (var i = 0; i < list.length; i++) if (list[i].code === region) r = list[i];
  var lines = r ? r.lines : country === "CA" ? [{ name: "GST", rate: 5 }] : [{ name: "Sales Tax", rate: 0 }];
  return {
    lines: lines.map(function (l) { return { name: l.name, rate: l.rate }; }),
    currency: country === "CA" ? "CAD" : "USD",
  };
}

// Clean up settings read from the database (or missing).
export function normalizeTax(row) {
  var src = row || {};
  var lines = Array.isArray(src.tax_lines || src.lines) ? (src.tax_lines || src.lines) : DEFAULT_TAX.lines;
  lines = lines
    .filter(function (l) { return l && String(l.name || "").trim(); })
    .slice(0, 3)
    .map(function (l) {
      var rate = parseFloat(l.rate);
      return { name: String(l.name).trim(), rate: isNaN(rate) ? 0 : rate, number: String(l.number || "").trim() };
    });
  var mode = (src.tax_mode || src.mode) === "included" ? "included" : "added";
  var cur = String(src.currency || "CAD").toUpperCase();
  return {
    country: src.tax_country || src.country || "CA",
    region: src.tax_region || src.region || null,
    lines: lines,
    mode: mode,
    currency: cur.length === 3 ? cur : "CAD",
  };
}

var CUR_LOCALE = { CAD: "en-CA", USD: "en-US", GBP: "en-GB", EUR: "en-IE", AUD: "en-AU", NZD: "en-NZ", INR: "en-IN", PKR: "en-PK", AED: "en-AE" };

// Money with the company currency symbol, for example $1,234.50 or £1,234.50
export function fmtMoney(n, currency, decimals) {
  var cur = String(currency || "CAD").toUpperCase();
  var d = decimals === undefined ? 2 : decimals;
  var num = Number(n) || 0;
  var opts = { style: "currency", currency: cur, minimumFractionDigits: d, maximumFractionDigits: d };
  try {
    return new Intl.NumberFormat(CUR_LOCALE[cur] || "en-CA", Object.assign({ currencyDisplay: "narrowSymbol" }, opts)).format(num);
  } catch (e) {
    try { return new Intl.NumberFormat(CUR_LOCALE[cur] || "en-CA", opts).format(num); } catch (e2) {}
  }
  return cur + " " + num.toFixed(d);
}

export function rateLabel(rate) {
  return String(Number(rate)) + "%";
}

function round2(x) { return Math.round((x + Number.EPSILON) * 100) / 100; }

// amounts: [{ amount, noTax }]. lines: [{ name, rate, number }]. mode: added or included.
// Added: tax goes on top of the prices. Included: the prices already contain the tax.
export function computeTax(amounts, lines, mode) {
  var taxable = 0, untaxed = 0;
  (amounts || []).forEach(function (a) {
    var v = parseFloat(a && a.amount);
    if (isNaN(v)) return;
    if (a.noTax) untaxed += v; else taxable += v;
  });
  var active = (lines || []).filter(function (l) { return parseFloat(l.rate) > 0; });
  var totalRate = active.reduce(function (s, l) { return s + parseFloat(l.rate); }, 0);
  var included = mode === "included";
  var base = included && totalRate > 0 ? taxable / (1 + totalRate / 100) : taxable;
  var taxes = active.map(function (l) {
    return { name: l.name, rate: parseFloat(l.rate), number: l.number || "", amount: round2(base * parseFloat(l.rate) / 100) };
  });
  var taxTotal = round2(taxes.reduce(function (s, t) { return s + t.amount; }, 0));
  var total, subtotal;
  if (included) {
    total = round2(taxable + untaxed);
    subtotal = round2(total - taxTotal);
  } else {
    subtotal = round2(taxable + untaxed);
    total = round2(subtotal + taxTotal);
  }
  return { mode: included ? "included" : "added", subtotal: subtotal, taxes: taxes, taxTotal: taxTotal, total: total, hasUntaxed: untaxed > 0 };
}

// "Total incl. GST" for one tax, "Total incl. tax" for several, "Total" for none.
export function totalLabel(taxes) {
  if (!taxes || taxes.length === 0) return "Total";
  if (taxes.length === 1) return "Total incl. " + taxes[0].name;
  return "Total incl. tax";
}
